import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import logo from "../assets/logo_no_background.png";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useLoginContext } from "../context/login.context";
import { login } from "../services/auth.service";
import requestLogin from "../services/login.service";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("Login feito com sucesso!");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const navigate = useNavigate();

  const { loginToken } = useLoginContext();

  const handleSubmit = (e: any) => {
    e.preventDefault();
    requestLogin(email, password)
      .then((x) => {
        return x.json();
      })
      .then((x) => {
        console.log(x);

        if (x.status === 200) {
          setAlertType("success");
          setAlertMessage("Login feito com sucesso!");
          setShowAlert(true);
        } else {
          setAlertType("error");
          if (x.message === "Unauthorized") {
            setAlertMessage("Email inválido!");
            setShowAlert(true);
          } else if (x.message === "Authentication failed. Wrong password") {
            setAlertMessage("Senha inválida!");
            setShowAlert(true);
          } else if (x.message) {
            setAlertMessage(x.message);
            setShowAlert(true);
          } else {
            setAlertMessage("Login deve ser feito com seu email!");
            setShowAlert(true);
          }
        }

        if (x.status === 200) {
          login(x.accessToken);
          loginToken(x.accessToken);

          localStorage.setItem("userInfo", JSON.stringify(x.user));
          navigate("/dashboard", { replace: true });
        }
      });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-2 sm:p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="flex justify-center">
          <div className="w-60 h-60 rounded-full bg-[#24324D] p-4 flex items-center justify-center">
            <img src={logo} alt="Logo" className="w-full h-full object-cover" />
          </div>
        </div>

        {/* Login Card */}
        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-3xl font-bold text-gray-900">
              Bem-vindo de volta
            </CardTitle>
            <CardDescription className="text-lg text-gray-600">
              Entre com suas credenciais para acessar sua conta
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Alert */}
            {showAlert && (
              <Alert
                className={
                  alertType === "error"
                    ? "border-red-200 bg-red-50"
                    : "border-green-200 bg-green-50"
                }
              >
                <AlertDescription
                  className={
                    alertType === "error"
                      ? "text-red-800 text-base"
                      : "text-green-800 text-base"
                  }
                >
                  {alertMessage}
                </AlertDescription>
              </Alert>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className="text-base font-medium text-gray-700"
                >
                  Endereço de Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11 text-base border-gray-200 focus:border-[#24324D] focus:ring-[#24324D]"
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="password"
                  className="text-base font-medium text-gray-700"
                >
                  Senha
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11 text-base border-gray-200 focus:border-[#24324D] focus:ring-[#24324D] pr-10"
                  />
                  <button
                    type="button"
                    aria-label={
                      showPassword ? "Ocultar senha" : "Mostrar senha"
                    }
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-2 flex items-center text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked === true)}
                  className="border-gray-300 data-[state=checked]:bg-[#24324D] data-[state=checked]:border-[#24324D]"
                />
                <Label
                  htmlFor="remember"
                  className="text-base text-gray-600 cursor-pointer"
                >
                  Manter Login
                </Label>
              </div>

              <Button
                type="submit"
                className="w-full h-11 text-base bg-[#24324D] hover:bg-[#1a2538] text-white font-medium transition-colors"
              >
                Iniciar Sessão
              </Button>
            </form>

            {/* Links */}
            <div className="space-y-4 text-center">
              <RouterLink
                to="/forgot-password"
                className="text-base text-[#24324D] hover:text-[#1a2538] hover:underline transition-colors"
              >
                Esqueceu sua senha?
              </RouterLink>

              <div className="text-base text-gray-600">
                Ainda não tem uma conta?{" "}
                <RouterLink
                  to="/signup"
                  className="text-[#24324D] hover:text-[#1a2538] hover:underline font-medium transition-colors"
                >
                  Cadastre-se
                </RouterLink>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
